import {
  ActionBarPrimitive,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useThreadRuntime,
} from "@assistant-ui/react";
import { useEffect, type FC, ComponentType } from "react";
import {
  ArrowDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  PencilIcon,
  RefreshCwIcon,
  SendHorizontalIcon,
} from "lucide-react";

import { cn } from "@components/assistant-ui/util";
import { Button } from "@components/ui/button";
import { TooltipIconButton } from "./tool-tip-icon-button";
import { MarkdownText } from "./markdown-text";
import { ToolFallbackWithAssertion } from "./tool-fallback";
import { ChartToolCall, ToolCallHandlers, ToolCallHandlersContext, ChartAutoRunContext } from "./chart-tool-call";

interface ThreadProps {
  initialQuestion?: string;
  WelcomeComponent?: ComponentType;
  toolCallHandlers?: ToolCallHandlers;
  /** Whether to auto-run charts when they appear. Set to false for historical threads. Default: true */
  autoRunCharts?: boolean;
}

export const Thread: FC<ThreadProps> = ({ initialQuestion, WelcomeComponent = ThreadWelcome, toolCallHandlers, autoRunCharts = true }) => {
  const threadRuntime = useThreadRuntime();

  useEffect(() => {
    if (initialQuestion) {
      threadRuntime.append({
          role: 'user',
          content: [{type: 'text', text: initialQuestion}]
      });
    }

  }, [threadRuntime, initialQuestion]);
  return (
    <ToolCallHandlersContext.Provider value={toolCallHandlers}>
      <ChartAutoRunContext.Provider value={autoRunCharts}>
        <ThreadPrimitive.Root
          className="aui-root aui-thread-root"
          style={{
            ["--thread-max-width" as string]: "42rem",
          }}
        >
          <ThreadPrimitive.Viewport className="aui-thread-viewport">
            <WelcomeComponent />

            <ThreadPrimitive.Messages
              components={{
                UserMessage: UserMessage,
                EditComposer: EditComposer,
                AssistantMessage: AssistantMessage,
              }}
            />

            <ThreadPrimitive.If empty={false}>
              <div className="aui-thread-viewport-spacer" />
            </ThreadPrimitive.If>

            <div className="aui-thread-viewport-footer">
              <ThreadScrollToBottom />
              <Composer />
            </div>
          </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
      </ChartAutoRunContext.Provider>
    </ToolCallHandlersContext.Provider>
  );
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  return (
    <ThreadPrimitive.Empty>
      <div className="aui-thread-welcome-root">
        <div className="aui-thread-welcome-center">
          <p className="aui-thread-welcome-message text-center">
            What do you want to know about your business?
          </p>
        </div>
        <ThreadWelcomeSuggestions />
      </div>
    </ThreadPrimitive.Empty>
  );
};

const ThreadWelcomeSuggestions: FC = () => {
  return (
    <div className="aui-thread-welcome-suggestions">
      <ThreadPrimitive.Suggestion
        className="aui-thread-welcome-suggestion"
        prompt="How do I use the assistant?"
        method="replace"
        autoSend
      >
        <span className="aui-thread-welcome-suggestion-text">
          How do I use the assistant?
        </span>
      </ThreadPrimitive.Suggestion>
      <ThreadPrimitive.Suggestion
        className="aui-thread-welcome-suggestion"
        prompt="What was our total revenue last year?"
        method="replace"
        autoSend
      >
        <span className="aui-thread-welcome-suggestion-text">
          What was our total revenue last year?
        </span>
      </ThreadPrimitive.Suggestion>
      
    </div>
  );
};

const Composer: FC = () => {
  return (
    <ComposerPrimitive.Root className="aui-composer-root">
      <ComposerPrimitive.Input
        rows={1}
        autoFocus
        placeholder="Write a message..."
        className="aui-composer-input"
      />
      <ComposerAction />
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC = () => {
  return (
    <>
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="Send"
            variant="default"
            className="aui-composer-send"
          >
            <SendHorizontalIcon />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            tooltip="Cancel"
            variant="default"
            className="aui-composer-cancel"
          >
            <CircleStopIcon />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root className="aui-user-message-root">
      <UserActionBar />
      <div className="aui-user-message-content">
        <MessagePrimitive.Parts />
      </div>
      <BranchPicker className="aui-user-branch-picker" />
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      autohide="always"
      className="aui-user-action-bar-root"
    >
      <ActionBarPrimitive.Edit className="aui-action-button">
        <PencilIcon className="aui-action-button-icon" />
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <ComposerPrimitive.Root className="aui-edit-composer-root">
      <ComposerPrimitive.Input className="aui-edit-composer-input" />

      <div className="aui-edit-composer-footer">
        <ComposerPrimitive.Cancel asChild>
          <Button variant="ghost">Cancel</Button>
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send asChild>
          <Button>Send</Button>
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
};

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root className="aui-assistant-message-root">
      <div className="aui-assistant-message-content">
        <MessagePrimitive.Parts components={{
          Text: MarkdownText,
          tools: {
            Fallback: ToolFallbackWithAssertion,
            by_name: {
              generate_chart_input: ChartToolCall,
              generate_and_run_analysis: ChartToolCall,
            }
          }
        }} />

        <MessagePrimitive.Error>
          <ErrorPrimitive.Root>
            <div className="alert alert-danger" role="alert">
              <ErrorPrimitive.Message />
            </div>
          </ErrorPrimitive.Root>
        </MessagePrimitive.Error>
      </div>
      <AssistantActionBar />
      <BranchPicker className="aui-assistant-branch-picker" />
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      autohide="not-last"
      className="aui-assistant-action-bar-root"
    >
      <ActionBarPrimitive.Copy className="aui-action-button">
        <MessagePrimitive.If copied>
          <CheckIcon className="aui-action-button-icon" />
        </MessagePrimitive.If>
        <MessagePrimitive.If copied={false}>
          <CopyIcon className="aui-action-button-icon" />
        </MessagePrimitive.If>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload className="aui-action-button">
        <RefreshCwIcon className="aui-action-button-icon" />
      </ActionBarPrimitive.Reload>
    </ActionBarPrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn("aui-branch-picker-root", className)}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};

const CircleStopIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      width="16"
      height="16"
    >
      <rect width="10" height="10" x="3" y="3" rx="2" />
    </svg>
  );
};